import type { Site } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import { useAuth } from './auth';
import { useT } from './i18n';
import { qk } from './queryClient';

export const SITE_STORAGE_KEY = 'morales.site';

interface SiteValue {
  /** every active site, the hub first; empty until loaded */
  sites: Site[];
  /** the site the screens are about; null = the hub (what the API defaults to) */
  siteId: string | null;
  site: Site | null;
  hub: Site | null;
  setSiteId: (id: string | null) => void;
}

const NO_SITES: SiteValue = { sites: [], siteId: null, site: null, hub: null, setSiteId: () => undefined };
const SiteContext = createContext<SiteValue | null>(null);

function readStored(): string | null {
  try {
    return window.localStorage.getItem(SITE_STORAGE_KEY);
  } catch {
    return null;
  }
}
function writeStored(id: string | null): void {
  try {
    if (id) window.localStorage.setItem(SITE_STORAGE_KEY, id);
    else window.localStorage.removeItem(SITE_STORAGE_KEY);
  } catch {
    // storage blocked: the choice lives in memory for this session
  }
}

/** Which site the stock screens are about. Remembered per phone; a user's home site is the first default. */
export function SiteProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const query = useQuery({ queryKey: qk.sites, queryFn: ({ signal }) => api.sites(signal), staleTime: 5 * 60_000, enabled: !!me && me.role !== 'customer' });
  const [chosen, setChosen] = useState<string | null>(() => readStored() ?? me?.homeSiteId ?? null);
  const setSiteId = useCallback((id: string | null) => {
    setChosen(id);
    writeStored(id);
  }, []);
  const value = useMemo<SiteValue>(() => {
    const sites = (query.data ?? []).filter((s) => s.active);
    const hub = sites.find((s) => s.kind === 'hub') ?? null;
    const site = sites.find((s) => s.id === chosen) ?? null;
    // A remembered site that no longer exists falls back to the hub.
    const siteId = site && hub && site.id !== hub.id ? site.id : null;
    return { sites, siteId, site: site ?? hub, hub, setSiteId };
  }, [query.data, chosen, setSiteId]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteValue {
  return useContext(SiteContext) ?? NO_SITES;
}

/** The site switch in the header: hidden while there is only the hub. */
export function SitePicker() {
  const t = useT();
  const { sites, site, setSiteId } = useSite();
  if (sites.length < 2) return null;
  return (
    <label className="site-picker">
      <span className="sr-only">{t('site')}</span>
      <select value={site?.id ?? ''} onChange={(e) => setSiteId(e.target.value || null)} aria-label={t('site')}>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </label>
  );
}
